import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AdjustmentsHorizontalIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import MultiSelectDropdown from './MultiSelectDropdown';
import FilterInfoLabel from './FilterInfoLabel';
import { useOpportunities } from '../contexts/OpportunityContext';
import { PRE_BID_MEETING_OPTIONS } from '../utils/preBidMeeting';
import { OPPORTUNITY_TYPE_LABELS } from '../utils/opportunityTypes';
import { Contract } from '../types';
import {
  getDisabledFacetOptions,
  getFacetCounts,
  OpportunityFilterKey,
  orderFacetOptions,
} from '../utils/opportunityFilters';

const OPPORTUNITY_TYPE_TOOLTIP =
  'The solicitation format requested by the procuring organization, such as Request for Proposal (RFP) or Request for Quote (RFQ), which indicates how vendors should structure and submit their response.';

type FilterKey = OpportunityFilterKey;

interface ContractFiltersProps {
  onFilterChange: (filters: {
    industries: string[];
    opportunityTypes: string[];
    procuringTypes: string[];
    preBidMeeting: string[];
    procuringOrganizations: string[];
  }) => void;
  appliedFilters: {
    industries: string[];
    opportunityTypes: string[];
    procuringTypes: string[];
    preBidMeeting: string[];
    procuringOrganizations: string[];
    searchTerm: string;
  };
  disabled?: boolean;
  opportunitiesOverride?: Contract[];
  variant?: 'expanded' | 'compact';
}

const ContractFilters: React.FC<ContractFiltersProps> = ({
  onFilterChange,
  appliedFilters,
  disabled = false,
  opportunitiesOverride,
  variant = 'expanded',
}) => {
  const [showMore, setShowMore] = useState(false);
  const [compactOpen, setCompactOpen] = useState(false);
  const compactPanelRef = useRef<HTMLDivElement>(null);
  const { filters, opportunities } = useOpportunities();
  const sourceOpportunities = opportunitiesOverride ?? opportunities;

  useEffect(() => {
    if (!compactOpen) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!compactPanelRef.current?.contains(event.target as Node)) {
        setCompactOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setCompactOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [compactOpen]);

  const opportunityTypeCoverage = useMemo(() => {
    const total = sourceOpportunities.length;
    const available = sourceOpportunities.filter((opportunity) => Boolean(opportunity.opportunity_type)).length;
    return { available, total };
  }, [sourceOpportunities]);

  const facetData = useMemo(() => {
    return {
      industries: getFacetCounts(sourceOpportunities, appliedFilters, 'industries'),
      opportunityTypes: getFacetCounts(sourceOpportunities, appliedFilters, 'opportunityTypes'),
      procuringTypes: getFacetCounts(sourceOpportunities, appliedFilters, 'procuringTypes'),
      preBidMeeting: getFacetCounts(sourceOpportunities, appliedFilters, 'preBidMeeting'),
      procuringOrganizations: getFacetCounts(sourceOpportunities, appliedFilters, 'procuringOrganizations'),
    };
  }, [appliedFilters, sourceOpportunities]);

  const sorted = useMemo(() => {
    if (opportunitiesOverride) {
      const unique = (values: Array<string | null | undefined>) =>
        Array.from(new Set(values.filter(Boolean) as string[])).sort();
      return {
        industries: unique(sourceOpportunities.map((contract) => contract.industry)),
        opportunityTypes: unique(sourceOpportunities.map((contract) => contract.opportunity_type)),
        procuringTypes: unique(sourceOpportunities.map((contract) => contract.procuring_type)),
        procuringOrganizations: unique(
          sourceOpportunities.map((contract) => contract.procuring_organization || contract.city)
        ),
      };
    }
    if (!filters) return null;
    return {
      industries: [...filters.industries].sort(),
      opportunityTypes: [...filters.opportunityTypes].sort(),
      procuringTypes: [...filters.procuringTypes].sort(),
      procuringOrganizations: [...filters.procuringOrganizations].sort(),
    };
  }, [filters, opportunitiesOverride, sourceOpportunities]);

  if (!sorted) {
    return <div>Loading filters...</div>;
  }

  const facetProps = (key: FilterKey, options: string[]) => {
    const facet = facetData[key];
    const orderedOptions = orderFacetOptions(options, facet.counts, appliedFilters[key]);
    return {
      options: orderedOptions,
      optionCounts: facet.counts,
      allOptionCount: facet.total,
      disabledOptions: getDisabledFacetOptions(orderedOptions, facet.counts, appliedFilters[key]),
    };
  };

  const updateFilter = (type: FilterKey, values: string[]) => {
    onFilterChange({
      ...appliedFilters,
      [type]: values,
    });
  };

  const clearAllFilters = () => {
    onFilterChange({ industries: [], opportunityTypes: [], procuringTypes: [], preBidMeeting: [], procuringOrganizations: [] });
  };

  const hasActiveFilters =
    appliedFilters.industries.length > 0 ||
    appliedFilters.opportunityTypes.length > 0 ||
    appliedFilters.procuringTypes.length > 0 ||
    appliedFilters.preBidMeeting.length > 0 ||
    appliedFilters.procuringOrganizations.length > 0;

  const activeFilterCount =
    appliedFilters.industries.length +
    appliedFilters.opportunityTypes.length +
    appliedFilters.procuringTypes.length +
    appliedFilters.preBidMeeting.length +
    appliedFilters.procuringOrganizations.length;

  const secondaryFilterCount =
    appliedFilters.opportunityTypes.length + appliedFilters.preBidMeeting.length;

  const activeFilters = [
    ...appliedFilters.industries.map((value) => ({ type: 'industries' as const, value, prefix: 'Industry' })),
    ...appliedFilters.procuringOrganizations.map((value) => ({ type: 'procuringOrganizations' as const, value, prefix: 'Organization' })),
    ...appliedFilters.procuringTypes.map((value) => ({ type: 'procuringTypes' as const, value, prefix: 'Organization type' })),
    ...appliedFilters.opportunityTypes.map((value) => ({ type: 'opportunityTypes' as const, value, prefix: 'Opportunity type' })),
    ...appliedFilters.preBidMeeting.map((value) => ({ type: 'preBidMeeting' as const, value, prefix: 'Pre-bid' })),
  ];

  const removeFilter = (type: FilterKey, value: string) => {
    updateFilter(type, appliedFilters[type].filter((item) => item !== value));
  };

  const filterControls = (inline = false) => (
    <>
      <div className={inline ? 'grid grid-cols-1 gap-3' : 'grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3'}>
        <MultiSelectDropdown
          label={
            <FilterInfoLabel
              label="Industries"
              tooltip="These are OppFoundry's service categories for the type of work being procured. They are not the buyer's industry."
            />
          }
          placeholder="All industries"
          {...facetProps('industries', sorted.industries)}
          values={appliedFilters.industries}
          onChange={(values) => updateFilter('industries', values)}
          disabled={disabled}
          searchable
          searchLabel="industries"
          layout={inline ? 'inline' : 'stacked'}
        />
        <MultiSelectDropdown
          label="Procuring Organization"
          placeholder="All organizations"
          {...facetProps('procuringOrganizations', sorted.procuringOrganizations)}
          values={appliedFilters.procuringOrganizations}
          onChange={(values) => updateFilter('procuringOrganizations', values)}
          disabled={disabled}
          searchable
          searchLabel="organizations"
          layout={inline ? 'inline' : 'stacked'}
        />
        <MultiSelectDropdown
          label="Organization Type"
          placeholder="All organization types"
          {...facetProps('procuringTypes', sorted.procuringTypes)}
          values={appliedFilters.procuringTypes}
          onChange={(values) => updateFilter('procuringTypes', values)}
          disabled={disabled}
          searchable
          searchLabel="organization types"
          layout={inline ? 'inline' : 'stacked'}
        />
      </div>

      <button
        type="button"
        onClick={() => setShowMore((current) => !current)}
        className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-500"
        aria-expanded={showMore}
      >
        {showMore ? 'Hide additional filters' : 'More filters'}
        {!showMore && secondaryFilterCount > 0 && (
          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">{secondaryFilterCount}</span>
        )}
        <ChevronDownIcon className={`h-4 w-4 transition-transform ${showMore ? 'rotate-180' : ''}`} />
      </button>

      {showMore && (
        <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 md:grid-cols-2">
          <div>
            <MultiSelectDropdown
              label={
                <FilterInfoLabel
                  label="Opportunity Type"
                  tooltip={OPPORTUNITY_TYPE_TOOLTIP}
                />
              }
              placeholder="All opportunity types"
              {...facetProps('opportunityTypes', sorted.opportunityTypes)}
              optionDescriptions={OPPORTUNITY_TYPE_LABELS}
              values={appliedFilters.opportunityTypes}
              onChange={(values) => updateFilter('opportunityTypes', values)}
              disabled={disabled}
            />
            {opportunityTypeCoverage.total > 0 && (
              <p className="mt-2 text-xs text-slate-500">
                Available on {opportunityTypeCoverage.available.toLocaleString()} of{' '}
                {opportunityTypeCoverage.total.toLocaleString()} opportunities in this view.
              </p>
            )}
          </div>
          <MultiSelectDropdown
            label="Pre-Bid Meeting"
            placeholder="All pre-bid statuses"
            {...facetProps('preBidMeeting', PRE_BID_MEETING_OPTIONS)}
            values={appliedFilters.preBidMeeting}
            onChange={(values) => updateFilter('preBidMeeting', values)}
            disabled={disabled}
            showAllOption={false}
          />
        </div>
      )}
    </>
  );

  if (variant === 'compact') {
    return (
      <div className="contents">
        <div className={`relative ${disabled ? 'pointer-events-none opacity-60' : ''}`} ref={compactPanelRef}>
          <button
            type="button"
            onClick={() => setCompactOpen((current) => !current)}
            className="inline-flex h-12 w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-blue-300 hover:text-blue-700 sm:min-w-36"
            aria-expanded={compactOpen}
          >
            <span className="inline-flex items-center gap-2">
              <AdjustmentsHorizontalIcon className="h-5 w-5" />
              Filters
              {activeFilterCount > 0 && (
                <span className="rounded-full bg-blue-600 px-2 py-0.5 text-xs text-white">{activeFilterCount}</span>
              )}
            </span>
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${compactOpen ? 'rotate-180' : ''}`} />
          </button>
          {compactOpen && (
            <div className="absolute right-0 z-30 mt-3 w-[min(32rem,calc(100vw-3rem))] rounded-xl border border-gray-200 bg-white p-4 shadow-2xl sm:p-5">
              {filterControls(true)}
            </div>
          )}
        </div>
        {hasActiveFilters && (
          <div className="mt-1 flex flex-wrap items-center gap-2 sm:col-span-2">
            {activeFilters.map((filter) => (
              <button
                key={`${filter.type}-${filter.value}`}
                type="button"
                onClick={() => removeFilter(filter.type, filter.value)}
                className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
                title={`Remove ${filter.prefix}: ${filter.value}`}
                aria-label={`Remove ${filter.prefix}: ${filter.value}`}
              >
                <span className="truncate">{filter.prefix}: {filter.value}</span>
                <XMarkIcon className="h-3.5 w-3.5 flex-none" />
              </button>
            ))}
            <button onClick={clearAllFilters} className="px-2 py-1.5 text-xs font-semibold text-blue-600 hover:text-blue-500">
              Clear all
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={disabled ? 'pointer-events-none opacity-60' : ''}>
      {filterControls()}
      {hasActiveFilters && (
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
          {activeFilters.map((filter) => (
            <button
              key={`${filter.type}-${filter.value}`}
              type="button"
              onClick={() => removeFilter(filter.type, filter.value)}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
              title={`Remove ${filter.prefix}: ${filter.value}`}
              aria-label={`Remove ${filter.prefix}: ${filter.value}`}
            >
              <span className="truncate">{filter.prefix}: {filter.value}</span>
              <XMarkIcon className="h-3.5 w-3.5 flex-none" />
            </button>
          ))}
          <button onClick={clearAllFilters} className="px-2 py-1.5 text-xs font-semibold text-blue-600 hover:text-blue-500">
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

export default ContractFilters;
